import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import {
  MenuNode,
  MenuRoot,
  MenuSecBarResult,
  MenuSectionMember,
  MenuSectionMembersResult,
  MenuSectionSummary,
  MenuTreeNode,
} from '@/client/types/menus';
import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';

const MENUS = '/api/admin/menus';

export const menuKeys = {
  all: ['menus-admin'] as const,
  lists: () => [...menuKeys.all, 'list'] as const,
  list: (q?: object) => [...menuKeys.lists(), q ?? {}] as const,
  tree: (menid_root?: number | null, includeInactive?: boolean) =>
    [...menuKeys.all, 'tree', menid_root ?? 'all', !!includeInactive] as const,
  roots: (includeInactive?: boolean) => [...menuKeys.all, 'roots', !!includeInactive] as const,
  detail: (menid: number) => [...menuKeys.all, 'detail', menid] as const,
  midSections: (includeInactive?: boolean) =>
    [...menuKeys.all, 'mid', !!includeInactive] as const,
  midSection: (section: string, includeInactive?: boolean) =>
    [...menuKeys.all, 'mid', section, !!includeInactive] as const,
  secBar: (pillar: string, secMenuActive?: boolean, includeInactive?: boolean) =>
    [...menuKeys.all, 'sec', pillar, !!secMenuActive, !!includeInactive] as const,
};

const qs = (params: Record<string, unknown>) => {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  });
  const s = p.toString();
  return s ? `?${s}` : '';
};

/** ff-api wraps payloads as `{ status, data }`. */
export const unwrapEnvelope = (raw: any): any => {
  if (raw != null && typeof raw === 'object' && 'status' in raw && 'data' in raw) {
    return raw.data;
  }
  return raw;
};

const asArray = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

// ─── System A: navigation tree ────────────────────────────────────────────────

export const useMenuRootsQuery = (opts?: { include_inactive?: boolean; enabled?: boolean }) => {
  const request = useClientStore((s) => s.request);
  const includeInactive = opts?.include_inactive ?? true;

  const fn = withErrorHandling(async (): Promise<MenuRoot[]> => {
    const raw = await request<any>(
      `${MENUS}/roots${qs({ include_inactive: includeInactive })}`,
    );
    const payload = unwrapEnvelope(raw);
    return asArray<MenuRoot>(payload?.roots ?? payload);
  }, 'Failed to load menu roots');

  return useQuery({
    queryKey: menuKeys.roots(includeInactive),
    queryFn: () => fn(),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    enabled: opts?.enabled !== false,
  });
};

export const useMenuTreeQuery = (opts?: {
  menid_root?: number | null;
  include_inactive?: boolean;
  enabled?: boolean;
}) => {
  const request = useClientStore((s) => s.request);
  const includeInactive = opts?.include_inactive ?? true;
  const menidRoot = opts?.menid_root ?? null;

  const fn = withErrorHandling(async (): Promise<MenuTreeNode[]> => {
    const params: Record<string, unknown> = { include_inactive: includeInactive };
    if (menidRoot != null) params.menid_root = menidRoot;
    const raw = await request<any>(`${MENUS}/tree${qs(params)}`);
    const payload = unwrapEnvelope(raw);
    return asArray<MenuTreeNode>(payload?.tree ?? payload);
  }, 'Failed to load menu tree');

  return useQuery({
    queryKey: menuKeys.tree(menidRoot, includeInactive),
    queryFn: () => fn(),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    enabled: opts?.enabled !== false && menidRoot != null,
    placeholderData: keepPreviousData,
  });
};

export const useMenuNodeQuery = (menid?: number | null) => {
  const request = useClientStore((s) => s.request);

  const fn = withErrorHandling(async (id: number): Promise<MenuNode> => {
    const raw = await request<any>(`${MENUS}/${id}`);
    return unwrapEnvelope(raw) as MenuNode;
  }, 'Failed to load menu node');

  return useQuery({
    queryKey: menuKeys.detail(menid ?? 0),
    queryFn: () => fn(menid!),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    enabled: menid != null && menid > 0,
  });
};

export const useMenuListQuery = (
  filters: {
    menid_root?: number;
    menid_parent?: number;
    active?: number | boolean;
    locale?: string;
    q?: string;
    limit?: number;
    offset?: number;
  } = {},
  opts?: { enabled?: boolean },
) => {
  const request = useClientStore((s) => s.request);

  const fn = withErrorHandling(async () => {
    const raw = await request<any>(`${MENUS}${qs(filters)}`);
    const payload = unwrapEnvelope(raw);
    const items = asArray<MenuNode>(
      payload?.menus ?? payload?.items ?? (Array.isArray(payload) ? payload : []),
    );
    const total =
      payload?.pagination?.total ??
      payload?.total ??
      raw?.total ??
      items.length;
    return { items, total, limit: filters.limit ?? 25, offset: filters.offset ?? 0 };
  }, 'Failed to load menu nodes');

  return useQuery({
    queryKey: menuKeys.list(filters),
    queryFn: () => fn(),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    enabled: opts?.enabled !== false,
    placeholderData: keepPreviousData,
  });
};

// ─── System B: mid sections ───────────────────────────────────────────────────

export const useMenuMidSectionsQuery = (opts?: {
  include_inactive?: boolean;
  enabled?: boolean;
}) => {
  const request = useClientStore((s) => s.request);
  const includeInactive = opts?.include_inactive;

  const fn = withErrorHandling(async (): Promise<MenuSectionSummary[]> => {
    const raw = await request<any>(
      `${MENUS}/mid${qs({ include_inactive: includeInactive })}`,
    );
    const payload = unwrapEnvelope(raw);
    return asArray<MenuSectionSummary>(payload?.sections ?? payload);
  }, 'Failed to load mid menu sections');

  return useQuery({
    queryKey: menuKeys.midSections(!!includeInactive),
    queryFn: () => fn(),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    enabled: opts?.enabled !== false,
  });
};

export const useMenuMidSectionMembersQuery = (
  section?: string | null,
  opts?: { include_inactive?: boolean; enabled?: boolean },
) => {
  const request = useClientStore((s) => s.request);
  const includeInactive = opts?.include_inactive;

  const fn = withErrorHandling(async (sec: string): Promise<MenuSectionMembersResult> => {
    const raw = await request<any>(
      `${MENUS}/mid/${sec}${qs({ include_inactive: includeInactive })}`,
    );
    const payload = unwrapEnvelope(raw);
    return {
      section: payload?.section ?? sec,
      flag_column: payload?.flag_column ?? '',
      members: asArray<MenuSectionMember>(payload?.members ?? []),
    };
  }, 'Failed to load section members');

  return useQuery({
    queryKey: menuKeys.midSection(section ?? '', !!includeInactive),
    queryFn: () => fn(section!),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    enabled: opts?.enabled !== false && !!section,
    placeholderData: keepPreviousData,
  });
};

// ─── System B: secondary category bar (per pillar) ────────────────────────────

export const useMenuSecBarQuery = (
  pillar?: string | null,
  opts?: {
    sec_menu_active?: boolean;
    include_inactive?: boolean;
    enabled?: boolean;
  },
) => {
  const request = useClientStore((s) => s.request);
  const secMenuActive = opts?.sec_menu_active ?? true;
  const includeInactive = opts?.include_inactive;

  const fn = withErrorHandling(async (p: string): Promise<MenuSecBarResult> => {
    const raw = await request<any>(
      `${MENUS}/sec/${p}${qs({
        sec_menu_active: secMenuActive,
        include_inactive: includeInactive,
      })}`,
    );
    const payload = unwrapEnvelope(raw);
    return {
      pillar: payload?.pillar ?? p,
      etid_root: payload?.etid_root ?? 0,
      hierarchy: payload?.hierarchy ?? payload,
    };
  }, 'Failed to load secondary category bar');

  return useQuery({
    queryKey: menuKeys.secBar(pillar ?? '', secMenuActive, !!includeInactive),
    queryFn: () => fn(pillar!),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    enabled: opts?.enabled !== false && !!pillar,
    placeholderData: keepPreviousData,
  });
};

/** Parallel sec-bar fetches — mirrors public header loading all pillars. */
export const useMenuSecBarsQuery = (
  pillars: string[],
  opts?: { sec_menu_active?: boolean; enabled?: boolean },
) => {
  const request = useClientStore((s) => s.request);
  const secMenuActive = opts?.sec_menu_active ?? true;

  return useQueries({
    queries: pillars.map((pillar) => ({
      queryKey: menuKeys.secBar(pillar, secMenuActive, false),
      queryFn: withErrorHandling(async (): Promise<MenuSecBarResult> => {
        const raw = await request<any>(
          `${MENUS}/sec/${pillar}${qs({ sec_menu_active: secMenuActive })}`,
        );
        const payload = unwrapEnvelope(raw);
        return {
          pillar: payload?.pillar ?? pillar,
          etid_root: payload?.etid_root ?? 0,
          hierarchy: payload?.hierarchy ?? payload,
        };
      }, `Failed to load ${pillar} category bar`),
      staleTime: DEFAULT_STALE_TIME,
      gcTime: DEFAULT_GC_TIME,
      enabled: opts?.enabled !== false && !!pillar,
    })),
  });
};
